import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import AuthContext from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';

export default function PurchaseKits() {
  const { user, tokens } = useContext(AuthContext);
  const { selectedPractice } = usePractice();
  const [kits, setKits] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const fetchQuote = async () => {
      if (!selectedPractice?.id || !tokens?.id_token) {
        setQuote(null);
        return;
      }
      setQuoteLoading(true);
      setQuoteError(null);
      try {
        const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
        const res = await fetch(`${apiBase}/qb/purchase-kits/quote`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens.id_token}`,
          },
          body: JSON.stringify({ quantity: kits, practice_id: selectedPractice.id }),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Error ${res.status}: ${res.statusText}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setQuote(data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching quote:', err);
          setQuote(null);
          setQuoteError(err.message);
        }
      } finally {
        if (!cancelled) {
          setQuoteLoading(false);
        }
      }
    };
    fetchQuote();
    return () => {
      cancelled = true;
    };
  }, [kits, selectedPractice?.id, tokens?.id_token]);

  const handleOrderClick = () => {
    setDialogOpen(true);
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  const handleCreateInvoice = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
      // Ensure a practice is selected
      if (!selectedPractice?.id) {
        throw new Error('No practice selected');
      }
      const res = await fetch(`${apiBase}/qb/purchase-kits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.id_token}`,
        },
        body: JSON.stringify({ quantity: kits, practice_id: selectedPractice.id }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
      const data = await res.json();
      // Close dialog, then navigate to Transactions highlighting this invoice
      setDialogOpen(false);
      navigate(`/transactions?realm_id=${encodeURIComponent(data.realm_id)}&invoice_id=${encodeURIComponent(data.invoice_id)}`);
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      // dialog already closed on success; keep open if error
    }
  };

  const handleKitsChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setKits(!isNaN(value) && value > 0 ? value : 1);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '--';
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const shippingAddress = useMemo(() => {
    const p = selectedPractice || {};
    const list = Array.isArray(p.addresses) ? p.addresses : [];
    const fromList =
      list.find(a => a?.purpose === 'shipping') ||
      list.find(a => a?.purpose === 'billing') ||
      list[0];
    const candidate = fromList || {
      street_1: p.shipping_street_1,
      street_2: p.shipping_street_2,
      city: p.shipping_city,
      state: p.shipping_state,
      zip_code: p.shipping_zip_code,
    };
    if (!candidate || !candidate.street_1) return null;
    const line1 = [candidate.street_1, candidate.street_2].filter(Boolean).join(', ');
    const line2 = [candidate.city, candidate.state].filter(Boolean).join(', ');
    const line3 = [line2, candidate.zip_code].filter(Boolean).join(' ');
    return {
      line1,
      line2: line3 || null,
    };
  }, [selectedPractice]);

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom>
        Purchase Kits
      </Typography>
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        V1
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>VeraBIND™ Tau</strong> is a first-in-class, blood-based assay that quantifies disease-driving hyperphosphorylated tau activity, giving clinicians an affordable, non-invasive tool for the early detection and diagnosis of Alzheimer’s disease; verified against tau-PET imaging with over 96 % overall agreement for confidence you can trust.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 2 }}>
        <TextField
          label="Number of Kits"
          type="number"
          size="small"
          InputProps={{ inputProps: { min: 1 } }}
          value={kits}
          onChange={handleKitsChange}
        />
        <Button variant="contained" color="primary" onClick={handleOrderClick}>
          Continue to Checkout
        </Button>
      </Box>
      <Box sx={{ mt: 2, mb: 2 }}>
        {quoteLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="textSecondary">
              Loading current pricing...
            </Typography>
          </Box>
        ) : (
          <>
            {quote && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Pricing summary
                </Typography>
                <Typography variant="body2">
                  Subtotal ({quote.quantity} kit{quote.quantity > 1 ? 's' : ''}): {formatCurrency(quote.subtotal)}
                </Typography>
                <Typography variant="body2">
                  Discount: -{formatCurrency(quote.discount)}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  Total: {formatCurrency(quote.total)}
                </Typography>
              </>
            )}
            {quoteError && (
              <Alert severity="error" sx={{ mt: quote ? 1 : 0 }}>
                Failed to load pricing: {quoteError}
              </Alert>
            )}
          </>
        )}
      </Box>
      <Dialog open={dialogOpen} onClose={handleCancel}>
        <DialogTitle>Confirm Purchase Kits</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to order {kits} kit{kits > 1 ? 's' : ''}. An invoice will be created and sent to your email address, {user?.email}. Items will be shipped according to your purchase agreement.
          </DialogContentText>
          <Divider sx={{ my: 2 }} />
          {quoteLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="textSecondary">
                Fetching current price...
              </Typography>
            </Box>
          ) : quote ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {Array.isArray(quote.line_items) && quote.line_items.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Line items
                  </Typography>
                  {quote.line_items.map((item, idx) => (
                    <Box key={`${item.sku}-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        {item.name || `SKU ${item.sku}`} × {quote.quantity}
                      </Typography>
                      <Typography variant="body2">{formatCurrency(item.line_subtotal)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="body2">
                Subtotal: {formatCurrency(quote.subtotal)}
              </Typography>
              <Typography variant="body2">
                Discount: -{formatCurrency(quote.discount)}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Total due: {formatCurrency(quote.total)}
              </Typography>
            </Box>
          ) : (
            <Alert severity="info">Pricing not available. Please try again.</Alert>
          )}
          {shippingAddress ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Shipping to
              </Typography>
              <Typography variant="body2">{shippingAddress.line1}</Typography>
              {shippingAddress.line2 && <Typography variant="body2">{shippingAddress.line2}</Typography>}
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No shipping address on file for this practice.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateInvoice}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
      {error && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {/* Navigation takes user to Transactions on success */}
    </Container>
  );
}
